<template>
  <div
    v-if="report"
    :class="{ 'border-grey-600 border': !report.hide_border }"
    class="group mx-auto flex h-full w-full flex-row justify-between rounded-lg bg-white shadow-pm"
    data-testid="report-card"
    :data-file-type="getReportFileType(report)"
  >
    <div class="relative h-full w-[125px] flex-shrink-0 rounded-l-lg bg-s-300">
      <img
        :src="$cloudinary.resize(videoImage, 'report-thumbnail')"
        class="flex h-[150px] max-h-[150px] w-full rounded-l-lg object-cover"
        :class="{ 'grayscale group-hover:grayscale-0': useGrayScaleBackground }"
        alt="Report ESG"
        data-testid="esg-video-card-image"
      />
      <CardLabel
        type="mp4"
        :show-label="false"
        class="absolute left-3 top-3 z-10"
      />
    </div>
    <div class="flex w-full flex-col justify-between p-3">
      <div>
        <div class="flex flex-col items-start justify-between gap-1">
          <h3
            class="line-clamp-2 text-sm font-semibold xl:text-lg"
            data-testid="forecasts-report-card-title"
          >
            {{ videoTitle }}
          </h3>
        </div>
      </div>
      <div
        class="flex w-full flex-col"
        :class="{ 'justify-between gap-y-1 lg:gap-y-4': reportHasAccess }"
      >
        <div
          v-if="reportHasAccess"
          class="container-button flex flex-row items-center justify-between gap-2"
          data-testid="services-group-buttons"
        >
          <CommonButton
            v-if="!videoIsValidate"
            class="mx-0 !w-full !cursor-default !text-sm lg:!min-w-[150px] 2xl:!max-w-[150px]"
            data-testid="esg-button-link"
            variant="primary"
            :show-spinner="false"
            disabled
          >
            Coming soon
          </CommonButton>
          <div v-if="videoIsValidate" class="flex gap-2">
            <CommonButton
              data-testid="esg-button-link"
              variant="primary"
              :link="link"
            >
              <div
                class="m-0 flex w-full flex-row items-center gap-x-1 p-0 lg:gap-x-1"
              >
                <IconPlay class="h-4 w-4 flex-shrink-0" />
                <span
                  v-if="showLabel"
                  class="hidden text-sm font-semibold xl:block"
                >
                  Watch Now
                </span>
              </div>
            </CommonButton>
            <ButtonDownload
              v-if="videoIsValidate"
              ref="esgButtonDownloadRef"
              class="!w-fit"
              data-testid="esg-button-download"
              :data-filename="videoTitle"
              :download-file="vimeoData"
              variant="secondary"
              :show-label="false"
            />
          </div>
          <IconCheckCircle
            v-if="reportHasAccess"
            class="h-8 w-8 stroke-p-900"
          />
        </div>
        <div v-else class="flex items-end justify-between">
          <ButtonContactUs
            text="Contact Us"
            variant="clean"
            :services="initialFields"
            class="!text-sm !text-black"
          />
          <IconLockOutline class="h-8 w-8 stroke-p-900" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import IconCheckCircle from '~/assets/icons/check-subscribe.svg'
import IconLockOutline from '~/assets/icons/LockOutline.svg'
import IconPlay from '~/assets/icons/play-circle.svg'
import CommonButton from '~/components/common/Button.vue'
import CardLabel from '~/components/portals/CardLabel.vue'
import ButtonContactUs from '~/components/shared/common/ButtonContactUs.vue'
import ButtonDownload from '~/components/shared/common/ButtonDownload.vue'

import { BUTTON_DOWNLOAD_ALL, SUSTAINABILITY } from '~/src/constants/markets'
import type { TReportProp } from '~/src/types/reports'
import { getReportFileType } from '~/src/utils/reports/image'
import { EventBus } from '~/utils/eventBus'
import { getMarketForRoute } from '~/src/utils/markets-with-prices'

const props = withDefaults(
  defineProps<{
    report?: TReportProp
    initialFields?: Array<any>
    product?: string
    hideLabel?: boolean
    useGrayScaleBackground?: boolean
    srcImage?: string
  }>(),
  {
    report: () => null,
    initialFields: () => [],
    product: 'string',
    hideLabel: false,
    useGrayScaleBackground: false,
    srcImage: '',
  },
)

const { $cloudinary } = useNuxtApp()
const reportValue = toValue(props.report) || {}
const esgButtonDownloadRef = ref(null)
const showLabel = computed(() => !props.report?.hide_label && !props.hideLabel)
const videoTitle = computed(() => props.report?.title)
const reportId = computed(() => props.report?.id)
const vimeoData = computed(() => ({
  ...reportValue,
  vimeoId: props.report?.file_name,
  isVideoVimeo: true,
}))
const videoImage = computed(
  () => props.srcImage || props.report?.images?.desktop,
)
const videoIsValidate = computed(() => !!props.report?.file_name)
const reportHasAccess = computed(() => props.report?.has_access || false)

const link = computed(() => {
  const queryParams = window?.location?.search || ''
  let marketType = props.report?.market_type || ''
  if (marketType === 'esg') marketType = SUSTAINABILITY
  if (marketType === 'forecast') marketType = 'forecasts'
  if (!reportId.value || !marketType) return ''
  const path = getMarketForRoute(props.product)

  const searchParams = new URLSearchParams(queryParams)
  searchParams.set('t', Date.now().toString())
  return `/${path}/data-reports/${marketType}/video/${reportId.value}?${searchParams.toString()}`
})

const handlerDownload = (_payload: string) => {
  if (_payload === BUTTON_DOWNLOAD_ALL && esgButtonDownloadRef.value) {
    esgButtonDownloadRef.value.$el.click()
  }
}

/**
 * This method was added to be able to interact with global events because
 * it was necessary to listen for an event from a sibling component, and the
 * traditional way to listen for events is between parent and child components.
 *
 * What this does is add a listener of the clickButtonCommon event,
 * and when it detects that this event has been issued, it verifies
 * that its payload is equal to "esg-button-download-all" and
 * executes the function that downloads all the reports from
 * the current esg page.
 */
onMounted(() => {
  EventBus.$on('clickButtonCommon', handlerDownload)
})

/**
 * What this does are remove the listener from the clickButtonCommon event
 * before the component is destroyed.
 */
onBeforeUnmount(() => {
  EventBus.$off('clickButtonCommon', handlerDownload)
})
</script>

<style>
@media (width <= 359px) {
  .container-button {
    flex-direction: column;
    row-gap: 4px;
  }
}
</style>
